import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Send, ArrowUpRight, Mail, ChevronDown, Copy, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  OUTREACH_STATUSES as CANONICAL_STATUSES,
  OUTREACH_STATUS_COLORS,
  ACTIVE_STATUSES as CANONICAL_ACTIVE_STATUSES,
  HIDDEN_STATUSES,
  getStatusColor,
  statusLabel,
  canonicalizeStatus,
} from '@/lib/statusColors';

const API = '/api';

// Filterable status list shown in the dropdown — drop the intro / early-stage
// rows since the user said: "i don't need any data from introductions, unless
// i have introduced the asset manually from accounts to any pharma".
const ALL_STATUSES = CANONICAL_STATUSES.filter(
  s => !['Introduction Made', 'Already in Touch'].includes(s)
);
// Matches the dashboard's "Active Conversations" KPI exactly.
const ACTIVE_STATUSES = CANONICAL_ACTIVE_STATUSES;
const STATUS_COLORS = OUTREACH_STATUS_COLORS;

export default function OutreachPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Accept either ?filter=active / ?filter=<single status> OR
  // ?status=A,B,C (comma-separated). The dashboard chips link via the
  // status= form so we honour both.
  const filterParam = searchParams.get('filter') || '';
  const statusParam = searchParams.get('status') || '';
  // Feb 2026 (v4) — the URL param may arrive in singular ("Meeting Scheduled")
  // or plural ("Meetings Scheduled") form depending on which dashboard chip
  // or link generated it. Normalize each incoming status via
  // canonicalizeStatus (accounts→outreach mapping) + a trailing-'s' strip so
  // it lands on the canonical singular form BEFORE the ALL_STATUSES gate.
  // Previously "Meetings Scheduled" bypassed the gate and fell through to
  // the unfiltered "All Conversations" view.
  const _canonForUrl = (s) => {
    if (!s) return s;
    const c = canonicalizeStatus(s);
    if (ALL_STATUSES.includes(c)) return c;
    // Try normalising trailing 's' from every word (handles both
    // "Meetings Scheduled" and "Meeting Scheduleds"):
    const words = c.split(' ');
    const candidates = new Set([c]);
    // Drop trailing 's' from first word, last word, all words
    candidates.add(canonicalizeStatus(c.replace(/s$/i, '')));
    if (words.length > 1) {
      const dropFirst = [words[0].replace(/s$/i, ''), ...words.slice(1)].join(' ');
      candidates.add(canonicalizeStatus(dropFirst));
      const dropAll = words.map(w => w.replace(/s$/i, '')).join(' ');
      candidates.add(canonicalizeStatus(dropAll));
    }
    for (const cand of candidates) {
      if (cand && ALL_STATUSES.includes(cand)) return cand;
    }
    // Case-insensitive match as final fallback
    const ci = ALL_STATUSES.find(x => x.toLowerCase() === c.toLowerCase());
    if (ci) return ci;
    return c;
  };
  const statusList = statusParam
    ? statusParam.split(',').map(s => _canonForUrl(s.trim())).filter(Boolean)
    : [];
  // Feb 2026 (v6) — accept `filter=<StageName>` (e.g. `?filter=Deal+Lost`)
  // as an alias of `?status=<StageName>`. Historically the filter= param
  // only carried the sentinel values "all" / "active"; stage names lived
  // under status=. But older bundles, external emails, and users typing
  // URLs manually all sometimes use filter= for the stage. Normalize:
  //   • "all" / "active"        → keep as sentinel filter mode
  //   • anything else           → try to canonicalize as a status
  const _SPECIAL_FILTER_MODES = new Set(['all', 'active', '']);
  const _canonicalFilterMode = (() => {
    const raw = (filterParam || '').trim();
    if (!raw) return '';
    if (_SPECIAL_FILTER_MODES.has(raw.toLowerCase())) return raw.toLowerCase();
    return _canonForUrl(raw);
  })();
  const initialFilter = _canonicalFilterMode
    || (statusList.length === 1 ? statusList[0] : '')
    || 'all';
  const [filterMode, setFilterMode] = useState(initialFilter);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES.includes(initialFilter) ? initialFilter : '');
  const [entries, setEntries] = useState([]);
  // Feb 2026 (v7) — Asset filter + view-mode toggle state (must be
  // declared BEFORE the entries-processing loop below so the loop can
  // read `assetFilter` without hitting a temporal-dead-zone error).
  const [assetFilter, setAssetFilter] = useState('__all__');
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'contact' ? 'contact' : 'grouped');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [loading, setLoading] = useState(true);
  // Feb 2026 v18 · biotech id→name lookup so the asset header shows
  // the true out-licenser (e.g. "Sun Novo") instead of falling back
  // to the pharma target's name (e.g. "Shanghai Henlius Biotech")
  // when the outreach row's cached biotech_company_name is empty.
  const [biotechNameById, setBiotechNameById] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/outreach?limit=50000`, { withCredentials: true });
        setEntries(res.data);
      } catch (err) {
        console.error('Failed to load outreach', err);
      } finally {
        setLoading(false);
      }
    })();
    // Fetch id→name map for biotech companies. Small payload (typically
    // <200 rows), one-time cost so every asset header can render the
    // correct out-licenser without an N+1 lookup.
    (async () => {
      try {
        const res = await axios.get(`${API}/biotech-companies?limit=5000`, { withCredentials: true });
        const map = {};
        (res.data || []).forEach(c => {
          if (c && c.id && c.company_name) map[c.id] = c.company_name;
        });
        setBiotechNameById(map);
      } catch (err) {
        console.warn('biotech lookup fetch failed (non-fatal):', err?.message);
      }
    })();
  }, []);

  // Conversation-level de-duplication: one row per (asset × biotech ×
  // pharma_contact) tuple, identified by its MOST-ADVANCED status across
  // all rows in that conversation. Same logic the backend uses for the
  // Outreach Overview chip counts, so when you click a chip and land here
  // the page total matches the chip exactly.
  // Aug 2026 full-funnel audit — ranks copied VERBATIM from
  // routes/dashboard.py STAGE_RANK so the max-rank fallback picks the
  // same winner on both sides (previously Data Shared / Term Sheet
  // Shared were missing here and ranked 0 in fallbacks).
  const STAGE_RANK = {
    'Deal Closed': 120, 'Term Sheet Shared': 110,
    'Deal Discussion': 100, 'Business Discussion': 95,
    'Due Diligence': 80, 'Awaiting Trial Data': 70,
    'Data Shared / Data Room Access': 65, 'CDA Signed': 60, 'CDA in Progress': 57,
    'Meeting Completed': 55, 'Meeting Scheduled': 50, 'Meeting Requested': 45,
    'Asset Under Evaluation': 40, 'Interest Shown': 30,
    'Awaiting reply': 25, 'Email Acknowledged': 20,
    'Already in Touch': 15, 'Introduction Made': 10, 'On Hold': 5,
    'Declined': -10, 'Deal Lost': -10, 'Ghosted': -20,
  };
  // Every Outreach Overview chip (Deal Discussion / AUE / Declined /
  // etc.) requires a tagged asset for trackability — a "Declined" or
  // "AUE" record with no asset is semantically meaningless.
  // Mirrors the backend ASSET_LEVEL_STATUSES set so chip count = page count.
  //
  // EXCEPTIONS (asset optional — legit relationship-level states):
  //   • Meeting Scheduled — Akums × KinoPharma first meeting.
  //   • Awaiting Trial Data — post-meeting "come back with data" state
  //     where the specific asset may not be pinned yet.
  //   • On Hold / Ghosted — passive account-level states.
  const ASSET_LEVEL_STATUSES = new Set([
    'Asset Under Evaluation', 'CDA Signed',
    'Data Shared / Data Room Access',
    'Due Diligence',
    'Deal Discussion', 'Term Sheet Shared',
    'Deal Closed', 'Deal Lost',
    'Declined',
  ]);
  // Feb 2026 (v4) — LATEST-STATUS-WINS dedup, matching the backend
  // `routes/dashboard.py` aggregation (`$sort updated_at DESC` + $first).
  // Previously the frontend used max-rank dedup, which diverged from the
  // dashboard: e.g. Regeneron × SHJ002-DED had 1 Ghosted (latest) + 2
  // Declined rows — backend chip = Ghosted (latest), frontend page =
  // Declined (higher rank), so clicking the "Ghosted" chip landed on an
  // empty page. Now both use the same rule.
  //
  // Fallback: when the LATEST row's status is a hidden/noise value
  // (Prospect Added, Email Acknowledged, Interest Shown, Introduction
  // Made, Already in Touch, Awaiting reply, Meeting Completed,
  // Business Discussion), fall back to max-rank on the non-noise rows
  // so a stale intro-stage last-touch doesn't erase a real Meeting
  // Scheduled / CDA Signed.
  const HIDDEN_FROM_LATEST = new Set([
    'Prospect Added', 'Email Acknowledged', 'Interest Shown',
    'Introduction Made', 'Already in Touch', 'Awaiting reply',
    'Meeting Completed', 'Business Discussion', '',
  ]);
  const _tsOf = (r) => r.updated_at || r.created_at || '';
  // Aug 2026 · canonical asset label (backend resolves loose labels like
  // "BHD1028" to the biotech's real asset record name) — used for ALL
  // grouping/dedup/count keys so name variants don't split groups.
  const canonAsset = (r) => (r.asset_name_canonical || r.asset_name || '').trim();
  // Two-pass: first collect ALL rows per (asset, biotech, pharma) key,
  // then pick the winner per the latest-wins-with-fallback rule.
  const convoRows = new Map();
  entries.forEach(rawE => {
    const canonical = canonicalizeStatus(rawE.status);
    if (!canonical || canonical === 'Prospect Added') return;
    const e = canonical === rawE.status ? rawE : { ...rawE, status: canonical };
    if (HIDDEN_STATUSES.has(e.status)) return;
    if (e.status === 'Email Acknowledged') return;
    // Aug 2026 full-funnel audit — mirror the dashboard's input gate
    // exactly: "Awaiting reply" is an internal auto-signal (excluded from
    // every dashboard count), and "Introduction Made" only counts when
    // account-driven (designation === "Account").
    if (e.status === 'Awaiting reply') return;
    if (e.status === 'Introduction Made' && (e.designation || '').trim().toLowerCase() !== 'account') return;
    // Feb 2026 (v7) — asset filter (user spec). Restrict to a single
    // molecule when the user has selected one from the Asset dropdown.
    if (assetFilter && assetFilter !== '__all__' && canonAsset(e) !== assetFilter) return;
    const pharmaKey = (e.company_name || '').trim().toLowerCase();
    const key = `${canonAsset(e)}|${e.biotech_company_id || ''}|${pharmaKey}`;
    if (!convoRows.has(key)) convoRows.set(key, []);
    convoRows.get(key).push(e);
  });
  const convoMap = new Map();
  convoRows.forEach((rows, key) => {
    // Sort DESC by ts so [0] is latest.
    const sorted = [...rows].sort((a, b) => _tsOf(b).localeCompare(_tsOf(a)));
    let winner = sorted[0];
    if (HIDDEN_FROM_LATEST.has(winner.status)) {
      // Fallback — pick max-rank among non-hidden rows.
      const visible = sorted.filter(r => !HIDDEN_FROM_LATEST.has(r.status));
      if (visible.length) {
        winner = visible.reduce((a, b) =>
          (STAGE_RANK[b.status] ?? 0) > (STAGE_RANK[a.status] ?? 0) ? b : a
        );
      }
    }
    convoMap.set(key, { ...winner, _email_count: rows.length });
  });
  // Feb 2026 (v4) — align to backend dashboard aggregation:
  //   • Backend dashboard.py excludes conversations for ASSET_LEVEL_STATUSES
  //     that have no asset_name attached (line ~479: "if not has_asset and
  //     canonical in ASSET_LEVEL_STATUSES: continue"). Mirror that here so
  //     the frontend page count matches the dashboard chip count exactly.
  //     Legitimate no-asset relationship states (Meeting Scheduled, Awaiting
  //     Trial Data, On Hold, Ghosted, Deal Closed on relationship level) are
  //     NOT in ASSET_LEVEL_STATUSES so they still surface.
  //   • Backend also excludes Email Acknowledged from the dashboard totals
  //     entirely (backend HIDDEN_STATUSES includes it). Include it here too
  //     when arriving from a dashboard chip so chip-to-page counts reconcile,
  //     but keep it in the dropdown for the "Filter by status" selector.
  const _hasAsset = (e) => !!(e.asset_name || '').trim();
  const allConversations = Array.from(convoMap.values()).filter(
    e => e.source === 'buyer_cda_doc'
      || !(ASSET_LEVEL_STATUSES.has(e.status) && !_hasAsset(e))
  );

  // Feb 2026 (v3) — production data sometimes stores the plural /
  // display form of the status ("Meetings Scheduled") instead of the
  // canonical singular ("Meeting Scheduled"). Normalize both sides of
  // the equality so the funnel-drill filter matches regardless of
  // whether the row was written from the funnel widget or the raw
  // canonical vocabulary.
  const _normStatus = (s) => canonicalizeStatus((s || '').replace(/s$/i, ''))
    || canonicalizeStatus(s || '')
    || (s || '');
  const _statusEq = (a, b) => {
    if (!a || !b) return a === b;
    if (a === b) return true;
    return _normStatus(a).toLowerCase() === _normStatus(b).toLowerCase()
      || canonicalizeStatus(a) === canonicalizeStatus(b);
  };

  // Step 2: apply the user's filter to the de-duped conversation set so the
  // total shown here equals the dashboard chip 1:1.
  const conversations = filterMode === 'active'
    ? allConversations.filter(e => ACTIVE_STATUSES.includes(e.status))
    : statusList.length > 1
      ? allConversations.filter(e => statusList.some(s => _statusEq(e.status, s)))
      : filterMode !== 'all' && ALL_STATUSES.includes(filterMode)
        ? allConversations.filter(e => _statusEq(e.status, filterMode))
        : allConversations;

  const filtered = conversations.sort(
    (a, b) => (b.last_action_date || b.created_at || '').localeCompare(a.last_action_date || a.created_at || '')
  );

  // Feb 2026 (v6) — align `rawFiltered` (used by the grouped render) with
  // `filtered` (used by the header summary count). Previously rawFiltered
  // showed ALL raw rows whose status matched the filter — which included
  // rows where the (asset, biotech, pharma) triple's LATEST status was
  // something else (e.g. GenePharma × AL-001 had 1 old Ghosted + 2 newer
  // AUE rows → the pair belongs in AUE, not Ghosted, but the raw Ghosted
  // row was still surfaced under the Ghosted filter, producing "summary
  // says 1, page renders 2" mismatches). Now we only surface raw rows
  // whose triple actually WON its convoMap dedup for this filter.
  const _winningTripleKeys = new Set(
    filtered.map(c => `${canonAsset(c)}|${c.biotech_company_id || ''}|${(c.company_name || '').trim().toLowerCase()}`)
  );
  const rawFiltered = entries.filter(e => {
    // Asset filter (v7) — same restriction the convoMap loop uses.
    if (assetFilter && assetFilter !== '__all__' && canonAsset(e) !== assetFilter) return false;
    const k = `${canonAsset(e)}|${e.biotech_company_id || ''}|${(e.company_name || '').trim().toLowerCase()}`;
    if (!_winningTripleKeys.has(k)) return false;
    if (filterMode === 'active') return ACTIVE_STATUSES.includes(canonicalizeStatus(e.status));
    if (statusList.length > 1) return statusList.some(s => _statusEq(e.status, s));
    if (filterMode !== 'all' && ALL_STATUSES.includes(filterMode)) return _statusEq(e.status, filterMode);
    return e.status && e.status !== 'Prospect Added';
  });

  // Inline status update — PATCH /outreach/{id}/status. Backend handles
  // the side-effects (follow-up creation/cancellation, terminal status
  // checks) so we just need to update the local table optimistically.
  const [savingStatusFor, setSavingStatusFor] = useState(null);
  const [savingDateFor, setSavingDateFor] = useState(null);

  // Feb 2026 (v7) — Asset filter + view-mode toggle (user spec).
  // • Asset filter narrows the conversation list to a single molecule so
  //   users can review outreach on an asset-by-asset basis.
  // • Grouped view (default) renders ONE row per (Pharma × Asset) tuple —
  //   the actual "BD opportunity" unit — expandable to show every contact
  //   at that opportunity. Contact view keeps the flat row-per-contact
  //   layout for detailed operational work.
  // (state hooks moved higher — see near `entries` state above.)
  const distinctAssets = useMemo(() => {
    const s = new Set();
    entries.forEach(e => {
      const a = (e.asset_name_canonical || e.asset_name || '').trim();
      if (a) s.add(a);
    });
    return Array.from(s).sort();
  }, [entries]);

  const copyEmail = async (email, e) => {
    e?.stopPropagation();
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied: ${email}`);
    } catch {
      // Fallback for non-https / older browsers
      const ta = document.createElement('textarea');
      ta.value = email;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success(`Copied: ${email}`); } catch { toast.error('Copy failed — long-press the address instead'); }
      document.body.removeChild(ta);
    }
  };

  const updateActionDate = async (outreachId, newDate) => {
    if (!newDate) return;
    setSavingDateFor(outreachId);
    const prev = entries;
    // Optimistic — convert the YYYY-MM-DD to an ISO at noon UTC for display
    const iso = `${newDate}T12:00:00+00:00`;
    setEntries(es => es.map(e => e.id === outreachId ? { ...e, updated_at: iso } : e));
    try {
      const res = await axios.patch(
        `${API}/outreach/${outreachId}/action-date`,
        { action_date: newDate },
        { withCredentials: true },
      );
      setEntries(es => es.map(e => e.id === outreachId ? { ...e, ...res.data } : e));
      toast.success('Date updated');
    } catch (err) {
      setEntries(prev);
      const msg = err?.response?.data?.detail || err?.message || 'Update failed';
      toast.error(typeof msg === 'string' ? msg : 'Update failed');
    } finally {
      setSavingDateFor(null);
    }
  };
  const updateStatus = async (outreachId, newStatus) => {
    setSavingStatusFor(outreachId);
    const prev = entries;
    setEntries(es => es.map(e => e.id === outreachId ? { ...e, status: newStatus } : e));
    try {
      const res = await axios.patch(
        `${API}/outreach/${outreachId}/status`,
        { status: newStatus },
        { withCredentials: true },
      );
      // Replace with the server's canonical row to pick up any
      // server-side enrichment (status_notes resets, timestamps, etc).
      setEntries(es => es.map(e => e.id === outreachId ? { ...e, ...res.data } : e));
      toast.success(`Status → ${newStatus}`);
    } catch (err) {
      setEntries(prev); // rollback
      const msg = err?.response?.data?.detail || err?.message || 'Update failed';
      toast.error(typeof msg === 'string' ? msg : 'Update failed');
    } finally {
      setSavingStatusFor(null);
    }
  };

  const setFilter = (mode) => {
    setFilterMode(mode);
    setStatusFilter(ALL_STATUSES.includes(mode) ? mode : '');
    if (mode === 'all') { setSearchParams({}); }
    else { setSearchParams({ filter: mode }); }
  };

  const pageTitle = filterMode === 'active' ? 'Active Conversations'
    : filterMode !== 'all' && ALL_STATUSES.includes(filterMode) ? statusLabel(filterMode)
    : 'All Conversations';

  const pageSubtitle = filterMode === 'active' ? 'Ongoing engagement — combined from direct biotech outreach + Accounts-driven conversations'
    : filterMode !== 'all' && ALL_STATUSES.includes(filterMode) ? `Filtered by status: ${statusLabel(filterMode)} — combined from biotech outreach + Accounts`
    : 'Unified BD pipeline — combined from direct biotech outreach + Accounts-driven conversations';

  // Asset-level stages where the dashboard funnel surfaces unique-asset
  // counts. We mirror the same set here so the in-page header reports
  // "N assets · M conversations" — keeping the dashboard chip and the
  // destination page in perfect parity (user 2026-02: "i don't see it
  // streamlining with anything inside").
  const STAGES_USE_ASSET_COUNT = new Set([
    'Asset Under Evaluation',
    'CDA Signed',
    'Data Shared / Data Room Access',
    'Awaiting Trial Data',
    'Due Diligence',
    'Deal Discussion',
    'Term Sheet Shared',
    'Deal Closed',
  ]);
  const showsAssetCount = ALL_STATUSES.includes(filterMode) && STAGES_USE_ASSET_COUNT.has(filterMode);
  const uniqueAssetCount = (() => {
    if (!showsAssetCount) return null;
    const s = new Set();
    filtered.forEach(c => {
      const a = canonAsset(c).toLowerCase();
      if (a) s.add(a);
    });
    return s.size;
  })();

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="outreach-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {pageTitle}
          </h1>
          <p className="text-base text-slate-500 mt-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {pageSubtitle}
          </p>
        </div>
        <a
          href="/outreach/consolidate"
          className="shrink-0 text-xs px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition flex items-center gap-1.5"
          data-testid="consolidate-link"
          title="Merge duplicate outreach rows from the same pharma × asset into a single consolidated conversation with multiple contacts."
        >
          <Users className="w-3.5 h-3.5" /> Consolidate duplicates
        </a>
      </div>

      {/* Filters — always visible so the user can quickly switch back to
          All / Active from any status-locked view. The asset-count
          summary is included on the right when the current filter is an
          asset-level stage, so the BD-funnel chip count is reproducible
          1:1 on this page (user 2026-02: "show the number of assets
          outside, conversation link inside"). */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
            filterMode === 'all' ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          data-testid="filter-all-conversations"
        >
          All ({allConversations.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
            filterMode === 'active' ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          data-testid="filter-active-conversations"
        >
          Active ({allConversations.filter(e => ACTIVE_STATUSES.includes(e.status)).length})
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <Select
          value={statusFilter}
          onValueChange={(v) => { if (v === '__clear__') { setFilter('all'); } else { setFilter(v); } }}
        >
          <SelectTrigger className="w-[220px] h-9 text-xs" data-testid="status-dropdown">
            <SelectValue placeholder="Filter by status..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__clear__">All Statuses</SelectItem>
            {ALL_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Feb 2026 (v7) — Asset filter (user spec). */}
        <Select
          value={assetFilter}
          onValueChange={(v) => setAssetFilter(v)}
        >
          <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="asset-dropdown">
            <SelectValue placeholder="Filter by asset..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Assets</SelectItem>
            {distinctAssets.map(a => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View-mode toggle: Grouped (Pharma × Asset opportunities) vs Contact. */}
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          <button
            onClick={() => { setViewMode('grouped'); setSearchParams(p => { const n = new URLSearchParams(p); n.delete('view'); return n; }); }}
            className={`px-2.5 py-1.5 text-[11px] transition-colors ${viewMode === 'grouped' ? 'bg-sky-50 text-sky-700 font-medium' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            data-testid="view-toggle-grouped"
          >Grouped</button>
          <button
            onClick={() => { setViewMode('contact'); setSearchParams(p => { const n = new URLSearchParams(p); n.set('view', 'contact'); return n; }); }}
            className={`px-2.5 py-1.5 text-[11px] border-l border-slate-200 transition-colors ${viewMode === 'contact' ? 'bg-sky-50 text-sky-700 font-medium' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            data-testid="view-toggle-contact"
          >Contact</button>
        </div>
        <span className="text-xs text-slate-400" data-testid="outreach-result-summary">
          {showsAssetCount && uniqueAssetCount !== null ? (
            <><strong className="text-slate-700 tabular-nums">{uniqueAssetCount}</strong> {uniqueAssetCount === 1 ? 'asset' : 'assets'} · {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}</>
          ) : (
            <>{filtered.length} conversation{filtered.length !== 1 ? 's' : ''}</>
          )}
        </span>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 shadow-sm" data-testid="outreach-table">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{filterMode !== 'all' ? 'No conversations match this filter' : 'No conversations yet'}</p>
            </div>
          ) : (viewMode === 'grouped' || filterMode === 'active' || (filterMode !== 'all' && ALL_STATUSES.includes(filterMode))) ? (
            /* Grouped view (Feb 2026 v7 · default) — ONE card per
             * (Pharma × Asset) opportunity, expandable to show every
             * contact at that opportunity. Previously this branch fired
             * only for status-filtered views; now it's the default for
             * ALL / Active too, mirroring how the dashboard funnel
             * already counts (Pharma × Asset = 1 opportunity).
             * The `contact` view mode (via ?view=contact) falls through
             * to the flat table below. */
            <div className="divide-y divide-slate-100" data-testid="outreach-asset-groups">
              {(() => {
                // Group raw rows (not de-duped conversations) by asset_name
                // so multiple contacts at the same pharma all surface.
                // Drop rows without an asset_name — asset-level statuses like
                // AUE / Meeting Scheduled / Declined are meaningless without
                // a tagged asset (user request 2026-02: "Untagged (no asset)?
                // i don't even know these companies — how come they have
                // come here?").
                //
                // NOTE (2026-02): Previously had an additional filter on
                // Deal Discussion / Due Diligence / Deal Closed requiring
                // each contact to have evidence of prior AUE / CDA in their
                // history. That over-filtered — EncuraGen × Faes Farma went
                // straight to Deal Discussion without an explicit AUE row,
                // and was hidden. Removed the evidence filter — if a row's
                // current status is "Deal Discussion", the contact is by
                // definition involved.
                const groups = new Map();
                const seenContactInGroup = new Map();
                rawFiltered.forEach(r => {
                  // For "relationship-level" statuses (where no specific
                  // asset needs to be pinned — Meeting Scheduled,
                  // Awaiting Trial Data, On Hold, Ghosted), keep untagged
                  // rows in a dedicated "Untagged asset" bucket instead
                  // of dropping them. Matches backend ASSET_LEVEL_STATUSES
                  // exception list. For everything else (AUE, CDA, Deal
                  // Disc, Declined), untagged rows are still dropped.
                  const RELATIONSHIP_STATUSES_ALLOW_UNTAGGED = new Set([
                    'Meeting Requested', 'Meeting Scheduled', 'CDA in Progress', 'Awaiting Trial Data',
                    'On Hold', 'Ghosted',
                  ]);
                  const rawAsset = (r.asset_name_canonical || r.asset_name || '').trim();
                  const allowUntaggedMS = (filterMode === 'active'
                      || filterMode === r.status)
                    && RELATIONSHIP_STATUSES_ALLOW_UNTAGGED.has(r.status);
                  // Buyer CDA documents synthesized by /api/outreach carry
                  // no asset_name but ARE the reason the dashboard counts
                  // this row in the CDA Signed chip — surface them under
                  // "Untagged asset" so the click-through isn't empty.
                  const allowUntaggedCDA = r.source === 'buyer_cda_doc';
                  const k = rawAsset || ((allowUntaggedMS || allowUntaggedCDA) ? '__untagged__' : '');
                  if (!k) return;
                  // Per-asset dedup by contact_email (or contact_name when
                  // email missing) — collapses true duplicate rows. When
                  // we hit a duplicate, prefer the account-sourced row
                  // (source==='accounts') so the VIA ACCOUNT badge is
                  // shown deterministically for account-driven meetings.
                  const dedupKey = `${k}|${(r.contact_email || '').toLowerCase().trim() || (r.contact_name || '').toLowerCase().trim()}|${(r.company_name || '').toLowerCase().trim()}|${(r.biotech_company_id || '')}`;
                  if (dedupKey && seenContactInGroup.has(dedupKey)) {
                    const existing = seenContactInGroup.get(dedupKey);
                    // Upgrade existing entry to account-sourced if this
                    // duplicate carries the source=accounts marker.
                    if (existing && r.source === 'accounts' && existing.source !== 'accounts') {
                      Object.assign(existing, r);
                    }
                    return;
                  }
                  if (!groups.has(k)) groups.set(k, []);
                  groups.get(k).push(r);
                  if (dedupKey) seenContactInGroup.set(dedupKey, r);
                });
                const _nm = (r) => `${(r.pharma_company_name || r.company_name || '').trim()}\u0000${(r.contact_name || '').trim()}`.toLowerCase();
                const sorted = Array.from(groups.entries())
                  .map(([k, rows]) => [k, [...rows].sort((a, b) => _nm(a).localeCompare(_nm(b)))])
                  .sort((a, b) => b[1].length - a[1].length);
                return sorted.map(([assetName, rows], idx) => {
                  const isUntagged = assetName === '__untagged__';
                  const displayAsset = isUntagged
                    ? (rows[0]?.source === 'buyer_cda_doc'
                        ? 'Finalised Buyer CDA — asset TBD'
                        : 'Meeting scheduled — asset TBD')
                    : assetName;
                  const biotechId = rows[0]?.biotech_company_id;
                  // Feb 2026 v18 · pick biotech name from the outreach
                  // row if populated; else look up by ID in the map we
                  // fetched at mount. NEVER fall back to `company_name`
                  // because that field on outreach is the PHARMA target
                  // (Shanghai Henlius) — surfacing it as the asset's
                  // owner produced the STC007/Sun-Novo confusion.
                  const biotechNameFromRow = rows.map(r => r.biotech_company_name).find(Boolean);
                  const biotechName = biotechNameFromRow || biotechNameById[biotechId] || '';
                  // Unique pharma companies evaluating this asset
                  const pharmas = new Set(rows.map(r => (r.company_name || '').toLowerCase()).filter(Boolean));
                  return (
                    <div key={assetName} className="p-5" data-testid={`asset-group-${assetName.replace(/[^a-z0-9]/gi, '-')}`}>
                      {/* Asset header — left badge is the sequential asset
                          index (1, 2, 3, ...) so the user can quickly see
                          how many assets are at this stage. Evaluator
                          count (e.g. "8 evaluators") lives in the
                          sub-label below. User 2026-02: "number them as
                          1, 2, 3 — number of assets being evaluated". */}
                      <div className="flex items-start gap-3 mb-4 pb-3 border-b border-slate-100">
                        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${isUntagged ? 'bg-slate-100' : 'bg-violet-50'}`}>
                          <span className={`text-[13px] font-bold tabular-nums ${isUntagged ? 'text-slate-500' : 'text-violet-700'}`}>{idx + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <h3 className={`text-base font-semibold ${isUntagged ? 'text-slate-600 italic' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {displayAsset}
                            </h3>
                            {!isUntagged && biotechId ? (
                              <a href={`/biotech/${biotechId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 hover:underline flex items-center gap-0.5">
                                {biotechName || '—'} <ArrowUpRight className="w-3 h-3" />
                              </a>
                            ) : !isUntagged ? (
                              <span className="text-xs text-slate-400">{biotechName || ''}</span>
                            ) : null}
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {pharmas.size} {pharmas.size === 1 ? 'pharma' : 'pharmas'} · {rows.length} {rows.length === 1 ? 'evaluator' : 'evaluators'}
                            {isUntagged && <span className="ml-2 text-amber-600">No asset locked in yet — exploratory meeting</span>}
                          </p>
                        </div>
                      </div>
                      {/* Evaluator rows */}
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                            <th className="text-left pb-2 pl-2">Pharma</th>
                            {isUntagged && <th className="text-left pb-2">Biotech</th>}
                            <th className="text-left pb-2">Contact</th>
                            <th className="text-left pb-2">Email</th>
                            <th className="text-left pb-2">Status</th>
                            <th className="text-left pb-2 pr-2">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {rows.map(r => {
                            const dateIso = r.updated_at || r.created_at || '';
                            const dateDisplay = dateIso ? new Date(dateIso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                            return (
                              <tr key={r.id} className="hover:bg-slate-50/40" data-testid={`asset-row-${r.id}`}>
                                <td className="py-2.5 pl-2 text-sm text-slate-700 font-medium">
                                  <span className="inline-flex items-center gap-1.5">
                                    {r.company_name || '—'}
                                    {r.source === 'accounts' && (
                                      <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5 rounded" title="Activity logged via the Accounts module">via Account</span>
                                    )}
                                  </span>
                                </td>
                                {isUntagged && (
                                  <td className="py-2.5 text-sm">
                                    {r.biotech_company_id ? (
                                      <a href={`/biotech/${r.biotech_company_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline inline-flex items-center gap-0.5">
                                        {r.biotech_company_name || '—'} <ArrowUpRight className="w-3 h-3" />
                                      </a>
                                    ) : (
                                      <span className="text-slate-500">{r.biotech_company_name || '—'}</span>
                                    )}
                                  </td>
                                )}
                                <td className="py-2.5 text-sm text-slate-600">
                                  <div>{r.contact_name || '—'}</div>
                                  {r.designation && r.designation.toLowerCase() !== 'account' && <div className="text-[11px] text-slate-400 leading-tight">{r.designation}</div>}
                                </td>
                                <td className="py-2.5">
                                  {r.contact_email ? (
                                    <div className="flex items-center gap-1.5">
                                      <a href={`mailto:${r.contact_email}`} className="text-[11px] text-sky-700 hover:underline break-all flex items-center gap-1">
                                        <Mail className="w-3 h-3 shrink-0" />{r.contact_email}
                                      </a>
                                      <button type="button" onClick={(e) => copyEmail(r.contact_email, e)} className="text-slate-400 hover:text-sky-700 shrink-0" title="Copy" data-testid={`asset-email-copy-${r.id}`}>
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : <span className="text-xs text-slate-400">—</span>}
                                </td>
                                <td className="py-2.5">
                                  <Badge variant="secondary" className={`text-[10px] ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>{r.status}</Badge>
                                </td>
                                <td className="py-2.5 pr-2 text-[11px] text-slate-500 whitespace-nowrap">{dateDisplay}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Country</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date of Action</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Asset</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech Company</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((entry) => {
                    // Build a unified contact list: primary (from
                    // entry.contact_*) + any consolidated contacts.
                    const allContacts = [
                      { name: entry.contact_name || '', title: entry.designation || '', country: entry.country || '', email: entry.contact_email || '', _primary: true },
                      ...((entry.contacts || []).map(c => ({
                        name: c.name || '', title: c.title || '', country: c.country || '', email: c.email || '', _primary: false,
                      })))
                    ].filter(c => c.name || c.email);
                    return (
                    <tr key={entry.id} className="hover:bg-slate-50/50" data-testid={`outreach-row-${entry.id}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="space-y-2">
                          {allContacts.map((c, idx) => (
                            <div key={idx} className={idx > 0 ? 'pt-2 border-t border-slate-100' : ''}>
                              <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                                {c.name || '—'}
                                {!c._primary && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1 py-0.5 rounded">also</span>}
                              </p>
                              {c.title && <p className="text-xs text-slate-400">{c.title}</p>}
                              {c.email && (
                                <div className="mt-0.5 flex items-center gap-1.5">
                                  <a
                                    href={`mailto:${c.email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 hover:underline break-all"
                                    data-testid={c._primary ? `outreach-email-${entry.id}` : `outreach-contact-email-${entry.id}-${idx}`}
                                    title={`Email ${c.email}`}
                                  >
                                    <Mail className="w-3 h-3 shrink-0" />
                                    {c.email}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={(e) => copyEmail(c.email, e)}
                                    className="text-slate-400 hover:text-sky-700 transition shrink-0"
                                    title="Copy email address"
                                    data-testid={c._primary ? `outreach-email-copy-${entry.id}` : `outreach-contact-copy-${entry.id}-${idx}`}
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 align-top">{entry.company_name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 align-top">
                        {allContacts.length > 1 ? (
                          <div className="space-y-2">
                            {allContacts.map((c, idx) => (
                              <div key={idx} className={idx > 0 ? 'pt-2 border-t border-slate-100' : ''}>
                                {c.country || '—'}
                              </div>
                            ))}
                          </div>
                        ) : (entry.country || '—')}
                      </td>
                      <td className="px-4 py-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-transparent hover:border-slate-300 hover:ring-1 hover:ring-slate-200 transition ${STATUS_COLORS[entry.status] || 'bg-slate-100 text-slate-600'} ${savingStatusFor === entry.id ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                              disabled={savingStatusFor === entry.id}
                              title="Click to change status"
                              data-testid={`outreach-status-btn-${entry.id}`}
                            >
                              {entry.status}
                              <ChevronDown className="w-3 h-3 opacity-60" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-56 p-1"
                            data-testid={`outreach-status-popover-${entry.id}`}
                          >
                            <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100 mb-1">
                              Update Status
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              {ALL_STATUSES.map(s => (
                                <button
                                  key={s}
                                  onClick={() => updateStatus(entry.id, s)}
                                  disabled={s === entry.status}
                                  className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-slate-50 flex items-center gap-2 ${s === entry.status ? 'bg-slate-50 font-medium cursor-default' : ''}`}
                                  data-testid={`outreach-status-opt-${entry.id}-${s.replace(/\s/g, '-')}`}
                                >
                                  <span className={`inline-block w-2 h-2 rounded-full ${(STATUS_COLORS[s] || 'bg-slate-300').split(' ')[0]}`} />
                                  {s}
                                  {s === entry.status && <span className="ml-auto text-emerald-600 text-xs">✓</span>}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {entry.status_notes && (
                          <p className={`text-[10px] italic leading-tight mt-1.5 max-w-[200px] ${
                            entry.status === 'Declined' ? 'text-rose-500' 
                            : entry.status === 'Awaiting Trial Data' ? 'text-orange-500' 
                            : entry.status === 'Closed - Successful' ? 'text-emerald-600'
                            : 'text-sky-600'
                          }`}>
                            {entry.status_notes}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3" data-testid={`outreach-date-cell-${entry.id}`}>
                        {(() => {
                          const iso = entry.updated_at || entry.created_at || '';
                          const dateOnly = iso ? iso.slice(0, 10) : '';
                          const display = dateOnly
                            ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—';
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border border-transparent hover:border-slate-300 hover:bg-slate-50 transition text-slate-700 ${savingDateFor === entry.id ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                                  disabled={savingDateFor === entry.id}
                                  title="Click to change action date"
                                  data-testid={`outreach-date-btn-${entry.id}`}
                                >
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  {display}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto p-3"
                                data-testid={`outreach-date-popover-${entry.id}`}
                              >
                                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">
                                  Action Date
                                </p>
                                <input
                                  type="date"
                                  defaultValue={dateOnly}
                                  max={new Date().toISOString().slice(0, 10)}
                                  onChange={(e) => updateActionDate(entry.id, e.target.value)}
                                  className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-sky-300"
                                  data-testid={`outreach-date-input-${entry.id}`}
                                />
                                <p className="text-[10px] text-slate-400 mt-1.5 max-w-[200px] leading-snug">
                                  Update this if the actual status change happened on a different day.
                                </p>
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        {entry.asset_name ? (
                          <Badge variant="secondary" className="text-[10px] bg-violet-50 text-violet-700" data-testid={`outreach-asset-${entry.id}`}>{entry.asset_name}</Badge>
                        ) : (
                          <span className="text-xs text-slate-400">{entry.molecule || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.biotech_company_id ? (
                          <a
                            href={`/biotech/${entry.biotech_company_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-sky-700 hover:underline flex items-center gap-1"
                          >
                            {entry.biotech_company_name || entry.company_name || '—'}
                            <ArrowUpRight className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-sm text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
